import {
  FENIX_BACKUP_FORMAT_VERSION,
} from './backup.ts';

import type {
  FenixBackupV1,
  LogicalBackupSnapshot,
} from './backup.ts';

import {
  validCheck,
  validId,
} from './daily-routine.ts';

import {
  validConsumption,
  validPlannedMeal,
  validRecipe,
  validRecipeRevision,
  validShoppingItem,
} from './nutrition.ts';

import {
  validWeight,
} from './progress.ts';

import {
  deriveSessionStatus,
  validExecutedSession,
  validExercise,
  validExerciseRevision,
  validPlannedSession,
  validRoutine,
  validRoutineRevision,
  validWorkingSet,
} from './training.ts';

import type {
  RestoreResult,
} from './restore.ts';

function failure<T = never>(
  code:
    | 'RESTORE_INVALID_BACKUP'
    | 'RESTORE_SCHEMA_FUTURE',
): RestoreResult<T> {
  return {
    ok: false,
    code,
  };
}

function isRecord(
  value: unknown,
): value is Record<string, unknown> {
  return (
    typeof value === 'object'
    && value !== null
    && !Array.isArray(value)
  );
}

function exactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
) {
  const actual =
    Object.keys(value).sort();

  const expected =
    [...keys].sort();

  return (
    actual.length === expected.length
    && actual.every(
      (key, index) =>
        key === expected[index],
    )
  );
}

function validInstant(
  value: unknown,
): value is string {
  return (
    typeof value === 'string'
    && value.length > 0
    && Number.isFinite(
      Date.parse(value),
    )
  );
}

function stable(
  value: unknown,
): string {
  return JSON.stringify(
    value,
    (_key, item) => {
      if (
        item
        && typeof item === 'object'
        && !Array.isArray(item)
      ) {
        return Object.fromEntries(
          Object.keys(item)
            .sort()
            .map(
              key => [
                key,
                item[key],
              ],
            ),
        );
      }

      return item;
    },
  );
}

function unique<T>(
  values: readonly T[],
  key: (value: T) => string,
) {
  const keys =
    values.map(key);

  return (
    new Set(keys).size
      === keys.length
  );
}

function validCurrentRevisionHistory<
  T extends {
    revision: number;
  },
>(
  current: readonly T[],
  history: readonly T[],
  id: (value: T) => string,
  allowPartialSuffix = false,
) {
  if (
    !unique(
      current,
      id,
    )
    || !unique(
      history,
      item =>
        id(item) + ':' + item.revision,
    )
  ) {
    return false;
  }

  const currentIds =
    new Set(
      current.map(id),
    );

  if (
    history.some(
      item =>
        !currentIds.has(
          id(item),
        ),
    )
  ) {
    return false;
  }

  const grouped =
    new Map<string, T[]>();

  for (const item of history) {
    const key = id(item);
    const revisions =
      grouped.get(key) ?? [];

    revisions.push(item);
    grouped.set(key, revisions);
  }

  return current.every(
    item => {
      const revisions =
        grouped.get(id(item)) ?? [];

      /*
       * A migrated ShoppingItem can predate its history store.
       * Empty history therefore means no revision has been
       * preserved yet. Missing earlier revisions are never
       * synthesized.
       */
      if (revisions.length === 0) {
        return allowPartialSuffix;
      }

      const ordered =
        [...revisions].sort(
          (a, b) =>
            a.revision - b.revision,
        );

      const first =
        ordered[0];

      if (first === undefined) {
        return false;
      }

      /*
       * Normal owners require a complete 1..N chain.
       *
       * ShoppingItem may preserve a migrated suffix F..N.
       * Once F exists, the suffix must remain contiguous.
       */
      const floor =
        allowPartialSuffix
          ? first.revision
          : 1;

      if (
        ordered.some(
          (revision, index) =>
            revision.revision
              !== floor + index,
        )
      ) {
        return false;
      }

      const latest =
        ordered[
          ordered.length - 1
        ];

      return (
        latest !== undefined
        && latest.revision
          === item.revision
        && stable(latest)
          === stable(item)
      );
    },
  );
}

function validDefinitionRevisionHistory<
  C,
  R extends {
    revision: number;
  },
>(
  current: readonly C[],
  history: readonly R[],
  currentId: (value: C) => string,
  historyId: (value: R) => string,
  currentRef: (value: C) => string,
  historyRef: (value: R) => string,
) {
  if (
    !unique(
      current,
      currentId,
    )
    || !unique(
      history,
      historyRef,
    )
    || !unique(
      history,
      item =>
        `${historyId(item)}:${item.revision}`,
    )
  ) {
    return false;
  }

  const currentIds =
    new Set(
      current.map(currentId),
    );

  if (
    history.some(
      item =>
        !currentIds.has(
          historyId(item),
        ),
    )
  ) {
    return false;
  }

  const grouped =
    new Map<string, R[]>();

  for (const item of history) {
    const key =
      historyId(item);

    const revisions =
      grouped.get(key) ?? [];

    revisions.push(item);
    grouped.set(key, revisions);
  }

  return current.every(
    item => {
      const revisions =
        grouped.get(
          currentId(item),
        ) ?? [];

      if (revisions.length === 0) {
        return false;
      }

      const ordered =
        [...revisions].sort(
          (left, right) =>
            left.revision
            - right.revision,
        );

      if (
        ordered.some(
          (revision, index) =>
            revision.revision
              !== index + 1,
        )
      ) {
        return false;
      }

      const latest =
        ordered[
          ordered.length - 1
        ];

      return (
        latest !== undefined
        && currentRef(item)
          === historyRef(latest)
      );
    },
  );
}

function logicalShape(
  value: unknown,
): value is FenixBackupV1 {
  if (!isRecord(value)) {
    return false;
  }

  if (
    !exactKeys(
      value,
      [
        'backupFormatVersion',
        'schemaVersion',
        'producerAppVersion',
        'backupId',
        'exportedAt',
        'timeContext',
        'dataset',
        'revisionHistory',
        'mediaManifest',
      ],
    )
  ) {
    return false;
  }

  if (
    !isRecord(value.timeContext)
    || !exactKeys(
      value.timeContext,
      [
        'zoneId',
        'utcOffsetMinutes',
      ],
    )
  ) {
    return false;
  }

  if (
    !isRecord(value.dataset)
    || !exactKeys(
      value.dataset,
      [
        'training',
        'nutrition',
        'progress',
        'dailyRoutine',
        'settings',
      ],
    )
  ) {
    return false;
  }

  const dataset =
    value.dataset;

  if (
    !isRecord(dataset.training)
    || !exactKeys(
      dataset.training,
      [
        'exercises',
        'routines',
        'plannedSessions',
        'executions',
        'workingSets',
      ],
    )
    || !isRecord(dataset.nutrition)
    || !exactKeys(
      dataset.nutrition,
      [
        'recipes',
        'plannedMeals',
        'consumptions',
        'shoppingItems',
      ],
    )
    || !isRecord(dataset.progress)
    || !exactKeys(
      dataset.progress,
      ['weights'],
    )
    || !isRecord(dataset.dailyRoutine)
    || !exactKeys(
      dataset.dailyRoutine,
      ['checks'],
    )
    || !isRecord(dataset.settings)
    || Object.keys(dataset.settings).length !== 0
  ) {
    return false;
  }

  if (
    !isRecord(value.revisionHistory)
    || !exactKeys(
      value.revisionHistory,
      [
        'dailyRoutine',
        'training',
        'nutrition',
        'progress',
      ],
    )
  ) {
    return false;
  }

  const history =
    value.revisionHistory;

  if (
    !isRecord(history.dailyRoutine)
    || !exactKeys(
      history.dailyRoutine,
      ['checks'],
    )
    || !isRecord(history.training)
    || !exactKeys(
      history.training,
      [
        'exercises',
        'routines',
        'plannedSessions',
      ],
    )
    || !isRecord(history.nutrition)
    || !exactKeys(
      history.nutrition,
      [
        'recipes',
        'plannedMeals',
        'consumptions',
        'shoppingItems',
      ],
    )
    || !isRecord(history.progress)
    || !exactKeys(
      history.progress,
      ['weights'],
    )
  ) {
    return false;
  }

  if (
    !isRecord(value.mediaManifest)
    || !exactKeys(
      value.mediaManifest,
      ['entries'],
    )
    || !Array.isArray(
      value.mediaManifest.entries,
    )
    || value.mediaManifest.entries.length !== 0
  ) {
    return false;
  }

  const arrays = [
    dataset.training.exercises,
    dataset.training.routines,
    dataset.training.plannedSessions,
    dataset.training.executions,
    dataset.training.workingSets,

    dataset.nutrition.recipes,
    dataset.nutrition.plannedMeals,
    dataset.nutrition.consumptions,
    dataset.nutrition.shoppingItems,

    dataset.progress.weights,
    dataset.dailyRoutine.checks,

    history.dailyRoutine.checks,

    history.training.exercises,
    history.training.routines,
    history.training.plannedSessions,

    history.nutrition.recipes,
    history.nutrition.plannedMeals,
    history.nutrition.consumptions,
    history.nutrition.shoppingItems,

    history.progress.weights,
  ];

  return arrays.every(
    Array.isArray,
  );
}

export function validateRestoreBackup(
  value: unknown,
): RestoreResult<FenixBackupV1> {
  if (!isRecord(value)) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const formatVersion =
    value.backupFormatVersion;

  if (
    typeof formatVersion === 'number'
    && Number.isInteger(formatVersion)
    && formatVersion
      > FENIX_BACKUP_FORMAT_VERSION
  ) {
    return failure(
      'RESTORE_SCHEMA_FUTURE',
    );
  }

  const schemaVersion =
    value.schemaVersion;

  if (
    typeof schemaVersion === 'number'
    && Number.isInteger(schemaVersion)
    && schemaVersion > 1
  ) {
    return failure(
      'RESTORE_SCHEMA_FUTURE',
    );
  }

  if (!logicalShape(value)) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const backup =
    value as FenixBackupV1;

  if (
    backup.backupFormatVersion
      !== FENIX_BACKUP_FORMAT_VERSION
    || backup.schemaVersion !== 1
    || typeof backup.producerAppVersion
      !== 'string'
    || backup.producerAppVersion.trim()
      .length === 0
    || !validId(backup.backupId)
    || !validInstant(backup.exportedAt)
    || typeof backup.timeContext.zoneId
      !== 'string'
    || backup.timeContext.zoneId.trim()
      .length === 0
    || !Number.isInteger(
      backup.timeContext.utcOffsetMinutes,
    )
    || backup.timeContext.utcOffsetMinutes
      < -840
    || backup.timeContext.utcOffsetMinutes
      > 840
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const currentChecks =
    backup.dataset.dailyRoutine.checks;

  const checkHistory =
    backup.revisionHistory
      .dailyRoutine.checks;

  if (
    !currentChecks.every(validCheck)
    || !checkHistory.every(validCheck)
    || !validCurrentRevisionHistory(
      currentChecks,
      checkHistory,
      check => check.checkId,
    )
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const exercises =
    backup.dataset.training.exercises;

  const exerciseHistory =
    backup.revisionHistory
      .training.exercises;

  if (
    !exercises.every(validExercise)
    || !exerciseHistory.every(
      validExerciseRevision,
    )
    || !validDefinitionRevisionHistory(
      exercises,
      exerciseHistory,
      item => item.exerciseId,
      item => item.exerciseId,
      item => item.currentRevisionRef,
      item => item.exerciseRevisionId,
    )
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const exerciseRevisions =
    new Map(
      exerciseHistory.map(
        revision => [
          revision.exerciseRevisionId,
          revision,
        ],
      ),
    );


  const routines =
    backup.dataset.training.routines;

  const routineHistory =
    backup.revisionHistory
      .training.routines;

  if (
    !routines.every(validRoutine)
    || !routineHistory.every(
      validRoutineRevision,
    )
    || !validDefinitionRevisionHistory(
      routines,
      routineHistory,
      item => item.routineId,
      item => item.routineId,
      item => item.currentRevisionRef,
      item => item.routineRevisionId,
    )
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const routineRevisions =
    new Map(
      routineHistory.map(
        revision => [
          revision.routineRevisionId,
          revision,
        ],
      ),
    );

  if (
    routineHistory.some(
      item =>
        item.exercises.some(
          ref =>
            !exerciseRevisions.has(
              ref.exerciseRevisionRef,
            ),
        ),
    )
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const sessions =
    backup.dataset.training
      .plannedSessions;

  const sessionHistory =
    backup.revisionHistory
      .training.plannedSessions;

  if (
    !sessions.every(
      validPlannedSession,
    )
    || !sessionHistory.every(
      validPlannedSession,
    )
    || !validCurrentRevisionHistory(
      sessions,
      sessionHistory,
      item => item.sessionId,
    )
    || !unique(
      sessions,
      item => item.plannedLocalDate,
    )
    || sessionHistory.some(
      item =>
        !routineRevisions.has(
          item.routineRevisionRef,
        ),
    )
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const sessionsById =
    new Map(
      sessions.map(
        session => [
          session.sessionId,
          session,
        ],
      ),
    );

  const sessionHistoryByKey =
    new Map(
      sessionHistory.map(
        session => [
          `${session.sessionId}:${session.revision}`,
          session,
        ],
      ),
    );

  const executions =
    backup.dataset.training.executions;

  if (
    !executions.every(
      validExecutedSession,
    )
    || !unique(
      executions,
      item => item.executionId,
    )
    || !unique(
      executions,
      item => item.plannedSessionId,
    )
    || executions.filter(
      item =>
        item.endedInstant === undefined,
    ).length > 1
    || executions.some(
      item => {
        const current =
          sessionsById.get(
            item.plannedSessionId,
          );

        const historical =
          sessionHistoryByKey.get(
            `${item.plannedSessionId}:${item.planRevisionAtStart}`,
          );

        return (
          !current
          || !historical
          || stable(current)
            !== stable(historical)
          || !deriveSessionStatus(
            current,
            item,
          ).ok
        );
      },
    )
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const executionsById =
    new Map(
      executions.map(
        execution => [
          execution.executionId,
          execution,
        ],
      ),
    );

  const workingSets =
    backup.dataset.training
      .workingSets;

  if (
    !workingSets.every(
      item =>
        Number.isSafeInteger(
          item.position,
        )
        && item.position >= 1
        && validWorkingSet(
          item.workingSet,
        ),
    )
    || !unique(
      workingSets,
      item =>
        item.workingSet.workingSetId,
    )
    || !unique(
      workingSets,
      item =>
        `${item.workingSet.executedSessionId}:${item.position}`,
    )
    || workingSets.some(
      item =>
        !executionsById.has(
          item.workingSet
            .executedSessionId,
        )
        || !exerciseRevisions.has(
          item.workingSet
            .exerciseRevisionRef,
        ),
    )
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const recipes =
    backup.dataset.nutrition.recipes;

  const recipeHistory =
    backup.revisionHistory
      .nutrition.recipes;

  if (
    !recipes.every(validRecipe)
    || !recipeHistory.every(
      validRecipeRevision,
    )
    || !validDefinitionRevisionHistory(
      recipes,
      recipeHistory,
      item => item.recipeId,
      item => item.recipeId,
      item => item.currentRevisionRef,
      item => item.recipeRevisionId,
    )
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const recipeRevisions =
    new Map(
      recipeHistory.map(
        revision => [
          revision.recipeRevisionId,
          revision,
        ],
      ),
    );

  const meals =
    backup.dataset.nutrition
      .plannedMeals;

  const mealHistory =
    backup.revisionHistory
      .nutrition.plannedMeals;

  if (
    !meals.every(validPlannedMeal)
    || !mealHistory.every(
      validPlannedMeal,
    )
    || !validCurrentRevisionHistory(
      meals,
      mealHistory,
      item => item.plannedMealId,
    )
    || mealHistory.some(
      item =>
        (
          item.recipeRevisionRef
            !== undefined
          && !recipeRevisions.has(
            item.recipeRevisionRef,
          )
        )
        || (
          item.trainingSessionId
            !== undefined
          && !sessionsById.has(
            item.trainingSessionId,
          )
        ),
    )
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const mealHistoryByKey =
    new Map(
      mealHistory.map(
        meal => [
          `${meal.plannedMealId}:${meal.revision}`,
          meal,
        ],
      ),
    );

  const consumptions =
    backup.dataset.nutrition
      .consumptions;

  const consumptionHistory =
    backup.revisionHistory
      .nutrition.consumptions;

  if (
    !consumptions.every(
      validConsumption,
    )
    || !consumptionHistory.every(
      validConsumption,
    )
    || !validCurrentRevisionHistory(
      consumptions,
      consumptionHistory,
      item => item.consumptionId,
    )
    || !unique(
      consumptions.filter(
        item =>
          item.plannedMealId
            !== undefined,
      ),
      item => item.plannedMealId!,
    )
    || consumptionHistory.some(
      item => {
        if (
          item.plannedMealId
            === undefined
        ) {
          return false;
        }

        if (
          item.plannedMealRevision
            === undefined
        ) {
          return true;
        }

        return !mealHistoryByKey.has(
          `${item.plannedMealId}:${item.plannedMealRevision}`,
        );
      },
    )
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const shopping =
    backup.dataset.nutrition
      .shoppingItems;

  const shoppingHistory =
    backup.revisionHistory
      .nutrition.shoppingItems;

  if (
    !shopping.every(
      validShoppingItem,
    )
    || !shoppingHistory.every(
      validShoppingItem,
    )
    || !validCurrentRevisionHistory(
      shopping,
      shoppingHistory,
      item => item.shoppingItemId,
      true,
    )
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  const weights =
    backup.dataset.progress.weights;

  const weightHistory =
    backup.revisionHistory
      .progress.weights;

  if (
    !weights.every(validWeight)
    || !weightHistory.every(
      validWeight,
    )
    || !validCurrentRevisionHistory(
      weights,
      weightHistory,
      item => item.observationId,
    )
  ) {
    return failure(
      'RESTORE_INVALID_BACKUP',
    );
  }

  return {
    ok: true,
    value:
      structuredClone(
        backup,
      ),
  };
}

export function validateRestoreSnapshot(
  snapshot: LogicalBackupSnapshot,
  backupId: string,
  exportedAt: string,
): RestoreResult<FenixBackupV1> {
  return validateRestoreBackup({
    backupFormatVersion:
      FENIX_BACKUP_FORMAT_VERSION,

    schemaVersion:
      snapshot.schemaVersion,

    producerAppVersion:
      'FENIX-restore-verifier',

    backupId,

    exportedAt,

    timeContext: {
      zoneId: 'UTC',
      utcOffsetMinutes: 0,
    },

    dataset:
      snapshot.dataset,

    revisionHistory:
      snapshot.revisionHistory,

    mediaManifest:
      snapshot.mediaManifest,
  });
}